import {FC, ReactElement, useMemo} from "react";
import {SimpleTreeView, TreeItem2, treeItemClasses} from "@mui/x-tree-view";
import {ChevronDownIcon, ChevronRightIcon} from "@interstate/components/Icons";
import {useInterstateToken} from "@interstate/components/InterstateThemeProvider";
import {CreatePlanNode} from "../../view-model";
import {collectSelectedPlanNodes} from "../../helpers";
import {usePlan} from "../../context";
import {CreatePlanTreeItemCheckbox} from "./CreatePlanTreeItemCheckbox";
import {CreatePlanNodeLabelType} from "./CreatePlanNodeLabelType";
import "./CreatePlanTreeView.scss";

interface CheckboxSlotProps {
  disabled?: boolean;
}

const checkboxSlotProps = (
  node: CreatePlanNode
): CheckboxSlotProps | undefined => {
  return {
    disabled: node.immutable
  };
};

export interface CreatePlanTreeViewProps {
  interactive: boolean;
  label: CreatePlanNodeLabelType;
}

export const CreatePlanTreeView: FC<CreatePlanTreeViewProps> = ({
  interactive,
  label: TreeItemLabel
}) => {
  const token = useInterstateToken();
  const {from, plan, onNodeSelectionToggle} = usePlan();
  const selectedItems = useMemo<string[]>(
    () => collectSelectedPlanNodes(plan),
    [plan]
  );

  const toTreeItem = (node: CreatePlanNode): ReactElement => {
    const prototype = node.caId === from.caId;
    const children = node.children ? [...node.children] : [];
    return (
      <TreeItem2
        key={node.caId}
        itemId={node.caId}
        data-testid={`create-plan-node-${node.caId}`}
        className={`create-tree-node create-plan-node${prototype ? " create-plan-node--from" : ""} create-plan-node--${node.immutable ? "immutable" : "mutable"}`}
        label={<TreeItemLabel node={node} />}
        slots={{
          checkbox: CreatePlanTreeItemCheckbox
        }}
        slotProps={{
          checkbox: checkboxSlotProps(node)
        }}>
        {children
          .filter(child => interactive || child.selected)
          .sort((a, b) => {
            // Always render the from node first
            if (a.caId === from.caId) return -1;
            if (b.caId === from.caId) return 1;
            // Then sort alphabetically by custName
            return a.source.custName.localeCompare(b.source.custName);
          })
          .map(toTreeItem)}
      </TreeItem2>
    );
  };

  return (
    <SimpleTreeView
      id={`create-plan-tree-view-${plan.caId}`}
      data-testid={`create-plan-tree-view-${plan.caId}`}
      className={`create-tree-view create-plan-tree-view create-plan-tree-view--${interactive ? "mutable" : "immutable"}`}
      sx={{
        [`& .${treeItemClasses.groupTransition}`]: {
          marginLeft: 1.9,
          borderLeft: theme => `1px solid ${theme.palette.divider}`
        }
      }}
      checkboxSelection={interactive}
      multiSelect={interactive}
      disableSelection={!interactive}
      disabledItemsFocusable={true}
      defaultExpandedItems={selectedItems}
      selectedItems={selectedItems}
      onItemSelectionToggle={onNodeSelectionToggle}
      slots={{
        expandIcon: ChevronRightIcon,
        collapseIcon: ChevronDownIcon
      }}
      slotProps={{
        collapseIcon: {
          color: token("sem.color.on-surface.primary")
        },
        expandIcon: {
          color: token("sem.color.on-surface.primary")
        }
      }}>
      {toTreeItem(plan)}
    </SimpleTreeView>
  );
};
